import React from 'react';
import { Box, VStack, Text, Spinner } from '@chakra-ui/react';
import { FormProvider, useFormContext } from './context/FormContext';
import { DonationFormUiProvider } from './context/UiContext';
import { StepNavigator } from './StepNavigator';
import { AmountStep } from './steps/AmountStep';
import { MonthlyUpsellStep } from './steps/MonthlyUpsellStep';
import { GiftStep } from './steps/GiftStep';
import { PaymentStep } from './steps/PaymentStep';
import { InformationStep } from './steps/InformationStep';
import { ReceiptStep } from './steps/ReceiptStep';
import { ChallengeProgress } from '../ChallengeProgress';
import type { DonationFormContentOverrides, DonationFormInitialSelection, DonationFormInitialStep, DonationFormPaymentOverrides, TierOverrides } from './types';

type DonationFormIdentifierProps =
  | { formId: string; formSlug?: never }
  | { formId?: never; formSlug: string }
  | { formId?: never; formSlug?: never; useDefault: true };

export type DonationFormProps = DonationFormIdentifierProps & {
  utmCampaign: string | null;
  sfmcSubkey?: string | null;
  /** GA4 donatePlacement for this form's donate/purchase events. */
  placement?: string;
  theme?: 'light' | 'dark';
  contentOverrides?: DonationFormContentOverrides | null;
  tierOverrides?: TierOverrides;
  paymentOverrides?: DonationFormPaymentOverrides;
  preview?: boolean;
  initialSelection?: DonationFormInitialSelection;
  initialStep?: DonationFormInitialStep;
  /**
   * When true, skip showing/executing reCAPTCHA.
   */
  disableCaptcha?: boolean;
  /**
   * When false, hides the step navigation UI (useful for kiosk/call-center flows).
   * Defaults to true.
   */
  showStepNavigator?: boolean;
  /**
   * When provided, the donation will be associated with this existing member.
   * This is used for admin-created donations where the member is pre-selected.
   */
  memberId?: number;
  /**
   * When provided, donor info fields are pre-populated from this member object directly,
   * skipping the admin-only `/donate/v1/members/:id` fetch. Used by signed-in self-service
   * flows where the auth context already has the full member detail.
   */
  prefillDonor?: import('@kuow/core').MemberDetailEntity | null;
  /**
   * Called when the Authorize.Net payment modal opens or closes.
   * Use this to disable parent dialog dismissal while the payment modal is active.
   */
  onAuthNetModalChange?: (isOpen: boolean) => void;
};

const DonationFormContent: React.FC<{ showStepNavigator: boolean }> = ({ showStepNavigator }) => {
  const { state } = useFormContext();
  const { currentStep, formDetails, isLoading, error } = state;

  if (isLoading) {
    return (
      <VStack gap={4} py={8}>
        <Spinner size="xl" color="teal.solid" />
        <Text color="fg.muted">Loading donation form...</Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <Box p={6} bg="red.subtle" borderRadius="md">
        <Text fontWeight="semibold" color="red.fg" mb={2}>
          Error Loading Form
        </Text>
        <Text color="red.fg">{error}</Text>
      </Box>
    );
  }

  if (!formDetails) {
    return null;
  }

  const hasGifts = formDetails.gifts.length > 0;
  const hasMonthlyUpsell = Boolean(formDetails.evergreen_upsell);

  const showChallengeBanner =
    Boolean(formDetails.matching_challenge) &&
    (currentStep === 'amount' || currentStep === 'monthly-upsell' || currentStep === 'gift' || currentStep === 'payment');

  return (
    <VStack align="stretch" overflow="visible">
      {showStepNavigator && currentStep !== 'receipt' ? <StepNavigator /> : null}

      {showChallengeBanner ? (
        <Box display="flex" justifyContent="center">
          <ChallengeProgress
            challenge={formDetails.matching_challenge!}
            showStars={true}
            showHeadline={true}
            primaryColor="river"
            secondaryColor="river.inverted"
            textColor="river.inverted"
          />
        </Box>
      ) : null}

      <Box overflow="visible">
        {currentStep === 'amount' && <AmountStep />}
        {currentStep === 'monthly-upsell' && hasMonthlyUpsell && <MonthlyUpsellStep />}
        {currentStep === 'gift' && hasGifts && <GiftStep />}
        {currentStep === 'payment' && <PaymentStep />}
        {currentStep === 'information' && <InformationStep />}
        {currentStep === 'receipt' && <ReceiptStep />}
      </Box>
    </VStack>
  );
};

export const DonationForm: React.FC<DonationFormProps> = (props) => {
  const { showStepNavigator = true, onAuthNetModalChange, ...formProviderProps } = props;
  return (
    <Box containerType="inline-size" w="full" overflow="visible">
      <Box
        w="full"
        minW="280px"
        maxW="lg"
        mx="auto"
        pt={{ base: 1, _cqMd: 2 }}
        px={{ base: 3, _cqMd: 6, _cqLg: 12 }}
        pb={{ base: 3, _cqMd: 6 }}
        overflow="visible"
      >
        <DonationFormUiProvider showStepNavigator={showStepNavigator} onAuthNetModalChange={onAuthNetModalChange}>
          <FormProvider {...formProviderProps}>
            <DonationFormContent showStepNavigator={showStepNavigator} />
          </FormProvider>
        </DonationFormUiProvider>
      </Box>
    </Box>
  );
};